import {useState, useRef,useEffect} from 'react'
import {
  select,
  scaleBand,
  axisBottom,
  axisLeft,
  scaleLinear,
  stack,
  max,
  event,
  map,
  InternSet,
  range,
  schemeTableau10,
  scaleOrdinal
} from "d3";

function wrap(text, width) {
  text.each(function () {
    var text = select(this),
      words = text.text().split(/\s+/).reverse(),
      word,
      line = [],
      lineNumber = 0,
      lineHeight = 1.1, // ems
      y = text.attr("y"),
      dy = parseFloat(text.attr("dy")),
      tspan = text
        .text(null)
        .append("tspan")
        .attr("x", 0)
        .attr("y", y)
        .attr("dy", dy + "em");
    while ((word = words.pop())) {
      line.push(word);
      tspan.text(line.join(" "));
      if (tspan.node().getComputedTextLength() > width) {
        line.pop();
        tspan.text(line.join(" "));
        line = [word];
        tspan = text
          .append("tspan")
          .attr("x", 0)
          .attr("dy", lineHeight + "em")
          .text(word);
      }
    }
  });
}

export default function VerticalStack(props){

    const svgRef = useRef();
    const wrapperRef = useRef();
    const canvasRef = useRef(null)
    
    const colors = schemeTableau10
    const [dkeys,setDkeys] = useState([])
    const [dcolors,setDcolors] = useState({})

    const maxLabelLength = props.data && props.data.length > 0
        ? Math.max(...props.data.map(d => (d.cn1 ? String(d.cn1).length : 0)))
        : 0;

    let chartWrapperHeight = 400;
    if (maxLabelLength > 100) {
        chartWrapperHeight = 520;
    } else if (maxLabelLength > 60) {
        chartWrapperHeight = 460;
    } else if (maxLabelLength > 30) {
        chartWrapperHeight = 430;
    }

    useEffect(()=>{
      if (!props.data || !props.data.length || !props.secondary) return;

      const svg = select(svgRef.current);
      const { width, height } = wrapperRef.current.getBoundingClientRect();
      if (!width || !height) return;

      const maxLabelLengthVal = props.data && props.data.length > 0
          ? Math.max(...props.data.map(d => (d.cn1 ? String(d.cn1).length : 0)))
          : 0;

      let dynamicMarginBottom = 100;
      if (maxLabelLengthVal > 100) {
          dynamicMarginBottom = 220;
      } else if (maxLabelLengthVal > 60) {
          dynamicMarginBottom = 180;
      } else if (maxLabelLengthVal > 30) {
          dynamicMarginBottom = 140;
      } else {
          dynamicMarginBottom = 110;
      }

      const margin = { top: 20, right: 20, bottom: dynamicMarginBottom, left: 50 }; // Increased bottom margin
      const chartHeight = height - margin.bottom; // Adjust chart height for legends
      
      // Get canvas reference first
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      // Process data for multi-bar graph
      let categories = [...new Set(props.data.map(d => d.cn1))];
      let subgroups = [...new Set(props.data.map(d => d[props.secondary]))];
      
      // Create processed data structure
      let processedData = categories.map(cat => {
          let obj = { category: cat };
          subgroups.forEach(sub => {
              const matchingItem = props.data.find(d => 
                  d.cn1 === cat && d[props.secondary] === sub
              );
              obj[sub] = matchingItem ? matchingItem.anscnt : 0;
          });
          return obj;
      });

      if (props.valueType === "%") {
          const total = processedData.reduce((sum, d) => sum + subgroups.reduce((subSum, sub) => subSum + (Number(d[sub]) || 0), 0), 0);
          processedData = processedData.map(d => {
              let obj = { category: d.category };
              subgroups.forEach(sub => {
                  obj[sub] = total > 0 ? parseFloat(((Number(d[sub]) || 0) / total * 100).toFixed(1)) : 0;
              });
              return obj;
          });
      }

      setDkeys(subgroups);

      // Set up scales
      const maxVal = max(processedData, d => max(subgroups, key => d[key])) || 1;
      const paddedMax = props.valueType === "%" ? Math.min(100, maxVal * 1.05) : maxVal * 1.05;

      const yScale = scaleLinear()
          .domain([0, paddedMax])
          .nice()
          .range([chartHeight, margin.top]);

      const xScale = scaleBand()
          .domain(categories)
          .range([margin.left, width - margin.right])
          .padding(0.2);

      // Create color scale
      const color = scaleOrdinal()
          .domain(subgroups)
          .range(colors.slice(0, subgroups.length));

      let cd = {};
      subgroups.forEach((key) => {
          cd[key] = color(key);
      });
      setDcolors(cd);

      // Set up inner scale for grouped bars
      const xSubScale = scaleBand()
          .domain(subgroups)
          .range([0, xScale.bandwidth()])
          .padding(0.05);

      // Clear previous content
      svg.selectAll('.content > *').remove();

      // Update axes
      const xAxisG = svg
          .select(".x-axis")
          .attr("transform", `translate(0, ${chartHeight})`)
          .call(axisBottom(xScale));

      xAxisG.selectAll("text")
          .style("font-size", "11px")
          .style("font-family", "Noto Sans")
          .call(wrap, Math.min(xScale.bandwidth(), 100));

      svg
          .select(".y-axis")
          .attr("transform", `translate(${margin.left}, 0)`)
          .call(axisLeft(yScale).tickFormat(d => props.valueType === "%" ? `${d}%` : d));

      // Create groups for each category
      const categoryGroups = svg
          .select(".content")
          .selectAll(".category")
          .data(processedData)
          .join("g")
          .attr("class", "category")
          .attr("transform", d => `translate(${xScale(d.category)}, 0)`);

      // Create bars
      categoryGroups.selectAll("rect")
          .data(d => subgroups.map(key => ({
              key,
              value: d[key]
          })))
          .join("rect")
          .attr("x", d => xSubScale(d.key))
          .attr("y", d => yScale(d.value))
          .attr("width", xSubScale.bandwidth())
          .attr("height", d => chartHeight - yScale(d.value))
          .attr("fill", d => cd[d.key]);

      // Add value labels
      categoryGroups.selectAll(".value-label")
          .data(d => subgroups.map(key => ({
              key,
              value: d[key]
          })))
          .join("text")
          .attr("class", "value-label")
          .text(d => d.value ? (props.valueType === "%" ? `${d.value}%` : d.value) : '')
          .attr("x", d => xSubScale(d.key) + xSubScale.bandwidth() / 2)
          .attr("y", d => yScale(d.value) - 5)
          .attr("text-anchor", "middle")
          .attr("font-family", "Noto Sans")
          .attr("font-size", "11px")
          .attr("fill", "black");

      // Add "Contribution" text
      svg
          .select(".content")
          .append("text")
          .attr("x", margin.left)
          .attr("y", height - 60)
          .attr("font-family", "Noto Sans")
          .attr("font-size", "12px")
          .text("Contribution");

      // Add legends
      const legendGroup = svg
          .select(".content")
          .append("g")
          .attr("class", "legend")
          .attr("transform", `translate(${margin.left}, ${height - 50})`);

      const legendsPerRow = Math.floor((width - margin.left - margin.right) / 120);
      
      const legends = legendGroup
          .selectAll(".legend-item")
          .data(subgroups)
          .join("g")
          .attr("class", "legend-item")
          .attr("transform", (d, i) => {
              const row = Math.floor(i / legendsPerRow);
              const col = i % legendsPerRow;
              return `translate(${col * 120}, ${row * 20})`;
          });

      // Add colored rectangles for legends
      legends
          .append("rect")
          .attr("width", 16)
          .attr("height", 16)
          .attr("fill", d => cd[d]);

      // Add legend text
      legends
          .append("text")
          .attr("x", 24)
          .attr("y", 12)
          .attr("font-family", "Noto Sans")
          .attr("font-size", "12px")
          .text(d => d);

      const svgEl = window.document.getElementById('svg_'+props.qid);
      if (!svgEl) return;
      let doctype = '<?xml version="1.0" standalone="no"?>'
      + '<!DOCTYPE svg PUBLIC "-//W3C//DTD SVG 1.1//EN" "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd">';

      let source = (new XMLSerializer()).serializeToString(svgEl);

      // Set canvas dimensions before creating image
      const totalHeight = height + 100;
      canvas.width = width;
      canvas.height = totalHeight;

      let img = new Image();
      img.src = 'data:image/svg+xml;base64,'+window.btoa(unescape(encodeURIComponent(doctype + source)))

      img.onload = function(){
      // Now that the image has loaded, put the image into a canvas element.
      context.clearRect(0, 0, width, totalHeight);
      context.drawImage(img, 0, 0,width,totalHeight);

      if(props.onReady != undefined){
          props.onReady(props.index, 2, canvas.toDataURL("image/png"), props.question)
        }
      }

    }, [props.data, props.valueType])

    return (
        <>
            <div
                ref={wrapperRef}
                style={{ width: "100%", height: `${chartWrapperHeight}px`, marginBottom: "2rem" }}
            >
                <svg ref={svgRef} style={{ width: "100%", height: "100%" }} id={'svg_'+props.qid}>
                    <g className="x-axis" />
                    <g className="y-axis" />
                    <g className="content" />
                </svg>
                <canvas ref={canvasRef} style={{display:'none'}}/>
            </div>
        </>
    );
}